import { useNavigate, useLocation } from 'react-router-dom'
import { useSelectedOrg } from '@/context/SelectedOrgContext'
import { useToast } from '@/context/ToastContext'
import type { OrgItem } from '@/lib/apiTypes'

/**
 * Switch the active org.
 *
 * The org slug is always the second path segment for org-scoped routes
 * (`/org/:slug/<section>/...`). We swap that segment and land on the section
 * ROOT, deliberately dropping any deeper params (a specific module/version or
 * workspace) - those resources may not exist in the new org, and preserving
 * them would produce 404s or stale detail views.
 *
 * Navigation alone updates the selection: OrgLayout reads the new URL slug and
 * syncs SelectedOrgContext. We don't touch context here to avoid double-writes.
 */
export function useSwitchOrg() {
  const navigate = useNavigate()
  const { pathname } = useLocation()

  return (newSlug: string) => {
    const segs = pathname.split('/') // ['', 'org', slug, section, ...]
    if (segs[1] === 'org' && segs[2] && segs[3]) {
      navigate(`/org/${newSlug}/${segs[3]}`)
    } else {
      // From an org-agnostic route (Dashboard / Integrations) → org home.
      navigate(`/org/${newSlug}/modules`)
    }
  }
}

/**
 * Land on an org that was just created. Awaits the org-list refetch FIRST so
 * OrgLayout (which validates the URL slug against the list) accepts the new
 * slug instead of bouncing back to the previous org. If the refetch fails or
 * the org still isn't listed, say so rather than navigating into that bounce.
 */
export function useOpenCreatedOrg() {
  const { refetchOrgs } = useSelectedOrg()
  const switchOrg = useSwitchOrg()
  const { showToast } = useToast()

  return async (org: Pick<OrgItem, 'slug' | 'name'>) => {
    const fresh = await refetchOrgs()
    if (fresh?.orgs.some((o) => o.slug === org.slug)) {
      switchOrg(org.slug)
    } else {
      showToast(`${org.name} was created - reload to open it.`, 'info')
    }
  }
}
